import { useCallback, useEffect, useRef } from 'react';

import type { WebSocketConnectionState } from '../../../hooks/useWebSocketConnection';
import { CrewService } from '../../../services/CrewService';
import { TelemetryPoint } from '../../../services/types/api.generated';
import { TelemetryData } from '../types';

interface UseTelemetryBackfillProps {
  sessionId: string;
  participantId: string;
  deviceUuid: string;
  connectionState: { telemetry: WebSocketConnectionState; ping: WebSocketConnectionState };
  latestCrewTelemetry: TelemetryData | null;
  onLinesUpdate: (lines: [number, number][]) => void;
}

export const useTelemetryBackfill = ({
  sessionId,
  participantId,
  deviceUuid,
  connectionState,
  latestCrewTelemetry,
  onLinesUpdate
}: UseTelemetryBackfillProps) => {
  const isInitialConnectionRef = useRef<boolean>(true);
  const lastKnownTelemetryTimestampRef = useRef<number | null>(null);
  const lastGapFetchTimestampRef = useRef<number | null>(null);
  const hasPerformedInitialBackfillRef = useRef<boolean>(false);

  const getRpiTimestamp = useCallback((point: TelemetryPoint): number => {
    if (point.raw_json && typeof point.raw_json === 'object') {
      const rawJson = point.raw_json as Record<string, unknown>;
      if (rawJson.timestamp && typeof rawJson.timestamp === 'object') {
        const timestamp = rawJson.timestamp as Record<string, unknown>;
        if (timestamp.utc_time_us_rpi && typeof timestamp.utc_time_us_rpi === 'string') {
          return new Date(timestamp.utc_time_us_rpi).getTime();
        }
      }
    }
    return new Date(point.timestamp).getTime();
  }, []);

  const processTelemetryToLines = useCallback(
    (telemetry: TelemetryPoint[]): [number, number][] => {
      if (!telemetry || telemetry.length === 0) {
        return [];
      }

      const sortedTelemetry = [...telemetry].sort((a, b) => {
        const timestampA = getRpiTimestamp(a);
        const timestampB = getRpiTimestamp(b);
        return timestampA - timestampB;
      });

      return sortedTelemetry
        .filter((point) => point.latitude !== null && point.longitude !== null)
        .map((point) => [parseFloat(point.longitude!), parseFloat(point.latitude!)]) as [number, number][];
    },
    [getRpiTimestamp]
  );

  const fetchAndMergeMissingTelemetry = async () => {
    if (!sessionId || !participantId || !deviceUuid) {
      return;
    }

    try {
      const missingTelemetry = await CrewService.getPastTelemetry(sessionId, participantId, deviceUuid);

      if (missingTelemetry && missingTelemetry.length > 0) {
        const sortedMissingTelemetry = [...missingTelemetry].sort((a, b) => {
          const timestampA = getRpiTimestamp(a);
          const timestampB = getRpiTimestamp(b);
          return timestampA - timestampB;
        });

        const missingLines = processTelemetryToLines(sortedMissingTelemetry);

        if (missingLines.length > 0) {
          onLinesUpdate(missingLines);
        }
      }
    } catch (error) {
      console.error('Failed to fetch missing telemetry:', error);
    }
  };

  useEffect(() => {
    const telemetryState = connectionState.telemetry;

    if (telemetryState === 'connected' && !isInitialConnectionRef.current) {
      const now = Date.now();
      const timeSinceLastFetch = lastGapFetchTimestampRef.current ? now - lastGapFetchTimestampRef.current : Infinity;

      if (timeSinceLastFetch > 2000) {
        lastGapFetchTimestampRef.current = now;
        fetchAndMergeMissingTelemetry();
      }
    }

    if (telemetryState === 'connected' && isInitialConnectionRef.current) {
      isInitialConnectionRef.current = false;
    }
  }, [connectionState.telemetry]);

  useEffect(() => {
    const timestampStr =
      latestCrewTelemetry?.timestamp?.utc_time_us_rpi || latestCrewTelemetry?.timestamp?.utc_time_us_pixhawk;
    if (timestampStr) {
      const timestamp = new Date(timestampStr).getTime();
      lastKnownTelemetryTimestampRef.current = timestamp;
    }
  }, [latestCrewTelemetry?.timestamp?.utc_time_us_rpi, latestCrewTelemetry?.timestamp?.utc_time_us_pixhawk]);

  useEffect(() => {
    const telemetryState = connectionState.telemetry;

    if (telemetryState === 'connected' && !hasPerformedInitialBackfillRef.current && participantId && deviceUuid) {
      hasPerformedInitialBackfillRef.current = true;

      const backfillTimeout = setTimeout(async () => {
        try {
          await fetchAndMergeMissingTelemetry();
        } catch (error) {
          console.error('Failed to backfill past telemetry:', error);
        }
      }, 1000);

      return () => {
        clearTimeout(backfillTimeout);
      };
    }
  }, [connectionState.telemetry, sessionId, participantId, deviceUuid]);
};
